import { StyleSheet } from 'react-native';

// Color palette — Figma §8.6
export const colors = {
  primary: '#336599',
  primaryDark: '#253c57',
  primaryLight: '#5c778f',
  accent: '#1CBB77',
  accentLight: '#7BFAC3',
  text: '#ffffff',
  textMuted: '#8eacc4',
  danger: '#e74c3c',
  dangerLight: '#4a1a1a',
  cardBg: '#1e3048',
  inputBg: '#162436',
  border: 'rgba(255,255,255,0.15)',
  borderStrong: 'rgba(255,255,255,0.25)',
  toggleOn: '#1CBB77',
  toggleOff: '#3d5a73',
  logicBadgeBg: '#1CBB77',
  unsavedColor: '#F8E96A',
};

// Avatar colors cycling by rule priority
export const avatarColors = ['#1CBB77', '#3498db', '#9b59b6', '#e67e22', '#1abc9c'];

const styles = StyleSheet.create({
  // ─── Section wrapper ─────────────────────────────────────────────────────
  section: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sectionTitle: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 18,
    color: colors.text,
    letterSpacing: 0.3,
  },

  // ─── Rule card ───────────────────────────────────────────────────────────
  ruleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginVertical: 4,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 3,
  },
  ruleCardActive: {
    backgroundColor: '#243d58',
    borderColor: colors.borderStrong,
    elevation: 8,
    shadowOpacity: 0.4,
  },
  ruleCardDisabled: {
    opacity: 0.55,
  },

  // Avatar circle (replaces plain priority badge)
  ruleAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  ruleAvatarText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 16,
  },
  ruleAvatarPriority: {
    fontFamily: 'Poppins_600SemiBold',
    color: 'rgba(255,255,255,0.7)',
    fontSize: 10,
    marginTop: 1,
  },

  dragHandle: {
    paddingRight: 10,
    color: colors.textMuted,
    fontSize: 20,
  },
  ruleName: {
    flex: 1,
    fontFamily: 'Poppins_600SemiBold',
    color: colors.text,
    fontSize: 15,
  },
  ruleCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },

  // Arrow buttons
  arrowButton: {
    padding: 5,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },

  // Overflow menu
  overflowMenu: {
    position: 'absolute',
    right: 0,
    top: '100%',
    backgroundColor: '#1e3048',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    zIndex: 9999,
    minWidth: 150,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 20,
  },
  overflowItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  overflowText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: colors.text,
  },
  overflowDanger: {
    color: colors.danger,
  },

  // ─── Editor container ─────────────────────────────────────────────────────
  editorContainer: {
    padding: 4,
    marginTop: 4,
  },

  unsavedBadge: {
    flex: 1,
    alignItems: 'center',
  },
  unsavedBadgeText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: '#F8E96A',
    textAlign: 'center',
  },

  // ─── Condition / Action rows ──────────────────────────────────────────────
  conditionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginVertical: 4,
    gap: 40,
    flexWrap: 'wrap',
    backgroundColor: 'rgba(30, 45, 61, 0.5)',
    borderRadius: 6,
    minHeight: 70,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginVertical: 4,
    gap: 40,
    flexWrap: 'wrap',
    backgroundColor: 'rgba(30, 45, 61, 0.5)',
    borderRadius: 6,
    minHeight: 70,
  },
  rowField: {
    flex: 1,
    minWidth: 100,
  },

// ─── "When" / "Then" heading row ──────────────────────────────────────────
  whenRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 18,
    marginBottom: 10,
  },
  whenWord: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 28,
    color: colors.text,
  },
  whenSubText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: '#E0E7EF',
  },

  // ─── Logic pill (ANY / ALL) ────────────────────────────────────────────────
  logicPill: {
    backgroundColor: '#1CBB77',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  logicPillText: {
    fontFamily: 'Poppins_700Bold',
    color: '#FFFFFF',
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ─── Legacy logic toggle (kept for compatibility) ─────────────────────────
  logicToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    flexWrap: 'wrap',
    gap: 6,
  },
  logicToggleText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.textMuted,
    fontSize: 14,
  },
  logicToggleButton: {
    backgroundColor: colors.logicBadgeBg,
    borderRadius: 5,
    paddingHorizontal: 12,
    paddingVertical: 5,
    minWidth: 48,
    alignItems: 'center',
  },
  logicToggleButtonText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 13,
    textTransform: 'uppercase',
  },

  // ─── Field label (above dropdowns) ────────────────────────────────────────
  fieldLabel: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 11,
    color: '#E0E7EF',
    marginBottom: 4,
  },

  // ─── Centered add button (Add Condition / Add Action) ─────────────────────
  addCenteredButton: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingTop: 10,
    paddingBottom: 0,
    marginTop: 4,
  },
  addCenteredInner: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  addCenteredText: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#E0E7EF',
    fontSize: 20,
  },

  // ─── Section labels ────────────────────────────────────────────────────────
  sectionLabel: {
    fontFamily: 'Poppins_500Medium',
    color: '#E0E7EF',
    fontSize: 14,
    marginBottom: 6,
    marginTop: 16,
  },

  // Condition section header row
  conditionSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
    marginTop: 16,
  },
  conditionSectionLabel: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.textMuted,
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginRight: 8,
  },

  // Action section header row
  actionSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    marginTop: 16,
  },
  actionSectionLabel: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.textMuted,
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginRight: 8,
    flex: 1,
  },

  // "For Each Instance" green badge
  forEachBadge: {
    backgroundColor: '#1CBB77',
    borderRadius: 12,
    paddingHorizontal: 7,
    paddingVertical: 6,
  },
  forEachBadgeText: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#FFFFFF',
    fontSize: 12,
    letterSpacing: 0.5,
  },

  // ─── Add buttons ──────────────────────────────────────────────────────────
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    gap: 4,
    alignSelf: 'flex-start',
  },
  addButtonText: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.accent,
    fontSize: 13,
  },

  // ─── Save/cancel buttons ──────────────────────────────────────────────────
  saveButton: {
    backgroundColor: colors.accent,
    borderRadius: 6,
    height: 44,
    minWidth: 160,
    paddingHorizontal: 28,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  saveButtonText: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.text,
    fontSize: 16,
  },
  cancelButton: {
    borderRadius: 6,
    height: 44,
    minWidth: 160,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#3D5A73',
  },
  cancelButtonText: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#FFFFFF',
    fontSize: 16,
  },
  deleteButton: {
    backgroundColor: colors.danger,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  deleteButtonText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.text,
    fontSize: 14,
  },

  // Footer
  editorFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    paddingTop: 16,
  },

  // ─── Error styles ──────────────────────────────────────────────────────────
  errorBorder: {
    borderColor: colors.danger,
    borderWidth: 1,
  },
  errorText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.danger,
    fontSize: 11,
    marginTop: 2,
  },

  // Validation summary
  validationSummary: {
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  validationSummaryTitle: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#FFFFFF',
    fontSize: 14,
    marginBottom: 4,
  },
  validationSummaryText: {
    fontFamily: 'Poppins_500Medium',
    color: '#FFFFFF',
    fontSize: 13,
  },

  // ─── Empty state ──────────────────────────────────────────────────────────
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyStateIcon: {
    fontSize: 48,
    color: colors.textMuted,
    marginBottom: 12,
  },
  emptyStateTitle: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 18,
    color: colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: colors.textMuted,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  emptyStateCTA: {
    backgroundColor: colors.accent,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 28,
    marginBottom: 24,
  },
  emptyStateCTAText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 15,
  },

  // Starter cards
  starterCard: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 8,
    padding: 14,
    marginVertical: 4,
    width: '100%',
    borderWidth: 1,
    borderColor: colors.border,
  },
  starterCardTitle: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.text,
    fontSize: 14,
  },
  starterCardDesc: {
    fontFamily: 'Poppins_500Medium',
    color: colors.textMuted,
    fontSize: 12,
    marginTop: 3,
    lineHeight: 18,
  },

  // ─── Name input ───────────────────────────────────────────────────────────
  nameInput: {
    backgroundColor: '#1E2D3D',
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 0,
    height: 44,
    color: '#FFFFFF',
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
  },

  // ─── Between inputs (smaller, side-by-side) ───────────────────────────────
  betweenInput: {
    backgroundColor: '#1E2D3D',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 0,
    height: 30,
    width: 150,
    color: '#FFFFFF',
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 15,
  },
  betweenAndText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 13,
    color: '#E0E7EF',
    alignSelf: 'center',
    marginHorizontal: 6,
  },

  // ─── Remove button ────────────────────────────────────────────────────────
  removeButton: {
    padding: 5,
    borderRadius: 4,
  },

  // ─── Disabled tooltip ─────────────────────────────────────────────────────
  tooltip: {
    backgroundColor: '#162436',
    borderRadius: 6,
    padding: 8,
    position: 'absolute',
    bottom: '100%',
    left: 0,
    zIndex: 9999,
    borderWidth: 1,
    borderColor: colors.border,
    minWidth: 200,
  },
  tooltipText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.textMuted,
    fontSize: 11,
  },

  // ─── Tag chip ─────────────────────────────────────────────────────────────
  tagChip: {
    backgroundColor: 'rgba(28, 187, 119, 0.2)',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: colors.accent,
  },
  tagChipText: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.accent,
    fontSize: 13,
  },

  // ─── Loading ──────────────────────────────────────────────────────────────
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.textMuted,
    fontSize: 14,
    marginTop: 12,
  },

  // ─── Error banner ─────────────────────────────────────────────────────────
  errorBanner: {
    borderRadius: 6,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    overflow: 'hidden',
  },
  errorBannerText: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#FFFFFF',
    fontSize: 14,
    flex: 1,
  },

  // ─── New Rule button (header) ─────────────────────────────────────────────
  newRuleButton: {
    backgroundColor: colors.accent,
    borderRadius: 7,
    paddingVertical: 8,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  newRuleButtonText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 13,
  },

  // ─── Add a Rule — bottom of list (Figma style) ────────────────────────────
  addRuleBottomButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 6,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  addRuleBottomText: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.accent,
    fontSize: 14,
  },

  // ─── Draft indicator (legacy — kept for compatibility) ────────────────────
  draftIndicator: {
    fontFamily: 'Poppins_500Medium',
    color: colors.unsavedColor,
    fontSize: 11,
    fontStyle: 'italic',
  },
});

export default styles;
